import { z } from 'zod';
import type { MysqlLockType } from '@/common/composables';
import { DB_TYPE } from '@/common/define/common.define';
import type {
  BaseObjectDetailAddition,
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sessionDetailSchema = z.object({
  threadId: z.coerce.number({ required_error: 'Thread ID', invalid_type_error: 'Thread ID' }),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
  init: nullishAsUndefined(z.boolean().optional()),
});

export const sqlDetailSchema = z.object({
  digest: z.string({ required_error: 'Digest' }).min(1, 'Digest'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  sqlId: nullishAsUndefined(z.string().optional()),
  sqlTextHashId: nullishAsUndefined(z.string().optional()),
  db: nullishAsUndefined(z.string().optional()),
  schema: nullishAsUndefined(z.string().optional()),
});

export const slowQuerySchema = z.object({
  threadId: z.coerce.number({ required_error: 'Thread ID', invalid_type_error: 'Thread ID' }),
  time: z.string({ required_error: 'Time' }).min(1, 'Time'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  sqlId: nullishAsUndefined(z.string().optional()),
  sqlTextHashId: nullishAsUndefined(z.string().optional()),
  digest: nullishAsUndefined(z.string().optional()),
});

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const objectDetailSchema = z.object({
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  objectName: z.string({ required_error: 'Object Name' }).min(1, 'Object Name'),
  databaseName: z.string({ required_error: 'Database Name' }).min(1, 'Database Name'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
});

export const documentTextSchema = z.object({
  sqlTextHashId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'SQL Text Hash ID' }).min(1, 'SQL Text Hash ID'),
  ),
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type SlowQuerySchema = z.infer<typeof slowQuerySchema>;
export type ObjectDetailSchema = z.infer<typeof objectDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type MysqlSessionDetailAddition = BaseSessionDetailAddition &
  SessionDetailSchema & {
    lockType?: MysqlLockType; // 값 있는 경우 lock info/ meta lock info 에서 연계
    isSlowQuery?: boolean; // ta > slow query 탭에서 연계 시 사용
  };
export type MysqlSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type MysqlSlowQueryAddition = SlowQuerySchema & {
  dbType: typeof DB_TYPE.MYSQL;
  userHost?: string;
  startTime: string;
  queryTime?: number;
  lockTime?: number;
  rowsSent?: number;
  rowsExamined?: number;
  sqlFullText?: string;
  schema?: string;
  period?: string;
};
export type MysqlObjectDetailAddition = BaseObjectDetailAddition & ObjectDetailSchema;
export type MysqlParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
